/**
 * 跨Tab通信工具类
 * 支持 BroadcastChannel 和 localStorage 降级方案
 */

export interface CrossTabMessage {
  type: string;
  data: any;
  timestamp: number;
  source: string;
}

export type MessageHandler = (message: CrossTabMessage) => void;

export class CrossTabCommunication {
  private channel: BroadcastChannel | null = null;
  private fallbackMode: 'broadcast' | 'storage' | 'none' = 'none';
  private messageHandlers: Map<string, MessageHandler[]> = new Map();
  private channelName: string;
  private sourceId: string;

  constructor(channelName: string = 'muset-cross-tab') {
    this.channelName = channelName;
    this.sourceId = `tab-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    this.initCommunication();
  }

  private initCommunication() {
    // 检测 BroadcastChannel 支持
    if (typeof BroadcastChannel !== 'undefined') {
      try {
        this.channel = new BroadcastChannel(this.channelName);
        this.fallbackMode = 'broadcast';
        this.setupBroadcastListener();
        console.log('✅ 使用 BroadcastChannel 进行跨Tab通信');
        return;
      } catch (error) {
        console.warn('⚠️ BroadcastChannel 创建失败，使用降级方案', error);
      }
    }

    // 降级到 localStorage + storage 事件
    this.fallbackMode = 'storage';
    this.setupStorageListener();
    console.log('✅ 使用 localStorage 进行跨Tab通信');
  }

  private setupBroadcastListener() {
    this.channel!.addEventListener('message', event => {
      const message = event.data as CrossTabMessage;
      // 忽略自己发送的消息
      if (message.source === this.sourceId) {
        return;
      }
      this.handleMessage(message);
    });
  }

  private setupStorageListener() {
    window.addEventListener('storage', event => {
      if (event.key === this.channelName && event.newValue) {
        try {
          const message = JSON.parse(event.newValue) as CrossTabMessage;
          // 忽略自己发送的消息
          if (message.source === this.sourceId) {
            return;
          }
          this.handleMessage(message);
          // 立即删除，避免存储积累
          localStorage.removeItem(this.channelName);
        } catch (error) {
          console.error('解析跨Tab消息失败:', error);
        }
      }
    });
  }

  private handleMessage(message: CrossTabMessage) {
    console.log('📨 收到跨Tab消息:', message);
    const handlers = this.messageHandlers.get(message.type) || [];
    handlers.forEach(handler => {
      try {
        handler(message);
      } catch (error) {
        console.error('处理跨Tab消息失败:', error);
      }
    });
  }

  /**
   * 发送消息到其他Tab
   */
  sendMessage(type: string, data: any) {
    const message: CrossTabMessage = {
      type,
      data,
      timestamp: Date.now(),
      source: this.sourceId,
    };

    console.log('📤 发送跨Tab消息:', message);

    if (this.fallbackMode === 'broadcast') {
      this.channel!.postMessage(message);
    } else if (this.fallbackMode === 'storage') {
      localStorage.setItem(this.channelName, JSON.stringify(message));
    }
  }

  /**
   * 监听特定类型的消息
   */
  onMessage(type: string, handler: MessageHandler) {
    if (!this.messageHandlers.has(type)) {
      this.messageHandlers.set(type, []);
    }
    this.messageHandlers.get(type)!.push(handler);

    // 返回取消监听的函数
    return () => {
      const handlers = this.messageHandlers.get(type);
      if (handlers) {
        const index = handlers.indexOf(handler);
        if (index > -1) {
          handlers.splice(index, 1);
        }
      }
    };
  }

  /**
   * 移除所有监听器
   */
  removeAllListeners() {
    this.messageHandlers.clear();
  }

  /**
   * 销毁通信实例
   */
  destroy() {
    this.removeAllListeners();
    if (this.channel) {
      this.channel.close();
      this.channel = null;
    }
  }

  /**
   * 获取通信模式
   */
  getMode(): 'broadcast' | 'storage' | 'none' {
    return this.fallbackMode;
  }

  /**
   * 获取当前Tab的ID
   */
  getSourceId(): string {
    return this.sourceId;
  }
}

// 单例实例
let crossTabInstance: CrossTabCommunication | null = null;

/**
 * 获取跨Tab通信实例
 */
export function getCrossTabCommunication(): CrossTabCommunication {
  if (!crossTabInstance) {
    crossTabInstance = new CrossTabCommunication();
  }
  return crossTabInstance;
}

/**
 * 销毁跨Tab通信实例
 */
export function destroyCrossTabCommunication() {
  if (crossTabInstance) {
    crossTabInstance.destroy();
    crossTabInstance = null;
  }
}

// 预定义的消息类型
export const CrossTabMessageTypes = {
  INDEXEDDB_ERROR: 'indexeddb-error',
  CLOSE_REQUEST: 'close-request',
  RELOAD_CONFIRM: 'reload-confirm',
  TAB_CLOSING: 'tab-closing',
} as const;
